import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #onQuantityChangeOptimistic = this.handleQuantityChangeOptimistic.bind(this);
  #isSyncing = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChangeOptimistic);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChangeOptimistic);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Handles optimistic update of cart UI before network request finishes.
   */
  handleQuantityChangeOptimistic(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const rows = this.refs.cartItemRows || [];
    const row = rows[line - 1];
    if (!row) return;

    // 1. Gather all items
    const items = [];
    rows.forEach((r, idx) => {
      let qty = parseInt(r.querySelector('quantity-selector-component input')?.value) || 0;
      if (idx === (line - 1)) {
        qty = quantity;
      }
      const price = parseInt(r.dataset.originalPrice) || 0;
      const isWaistChain = r.dataset.isWaistChain === 'true';
      if (qty > 0) {
        items.push({ qty, price, isWaistChain });
      }
    });

    // 2. Format function
    const format = this.dataset.moneyFormat || "{{amount}}";
    const currency = this.dataset.currency || "INR";
    const formatMoneyValue = (amount) => formatMoney(amount, format, currency);

    // 3. Calculate new values
    const waistPrices = [];
    const regularPrices = [];
    items.forEach(item => {
      for (let i = 0; i < item.qty; i++) {
        if (item.isWaistChain) {
          waistPrices.push(item.price);
        } else {
          regularPrices.push(item.price);
        }
      }
    });

    const totalItems = waistPrices.length + regularPrices.length;
    const originalSubtotal = [...waistPrices, ...regularPrices].reduce((sum, p) => sum + p, 0);

    let totalDiscount = 0;
    let dealCode = "";
    let activeDealName = "";
    let nextDealProgress = 0;
    let nextDealMsg = "";
    let offersIcon = "🎉";

    // Waist chain B1G1 calculation (cheaper free per pair)
    waistPrices.sort((a, b) => a - b);
    const numWaistPairs = Math.floor(waistPrices.length / 2);
    let waistDiscount = 0;
    for (let i = 0; i < numWaistPairs; i++) {
      waistDiscount += waistPrices[i];
    }

    // Regular items tiered bundle calculation
    regularPrices.sort((a, b) => a - b);
    const numGroupsOf5 = Math.floor(regularPrices.length / 5);
    const remainder = regularPrices.length % 5;

    let regularPaid = 0;
    if (numGroupsOf5 > 0) {
      regularPaid += numGroupsOf5 * 159900;
    }
    if (remainder === 4) {
      regularPaid += 129900;
    } else if (remainder === 3) {
      regularPaid += 89900;
    } else if (remainder === 2) {
      regularPaid += regularPrices[1] || 0;
    } else if (remainder === 1) {
      regularPaid += regularPrices[0] || 0;
    }

    const regularOriginalSubtotal = regularPrices.reduce((sum, p) => sum + p, 0);
    const regularDiscount = Math.max(0, regularOriginalSubtotal - regularPaid);

    totalDiscount = waistDiscount + regularDiscount;
    const customSubtotal = originalSubtotal - totalDiscount;

    // Set deal code (comma-separated list of all applied coupon codes)
    let dealCodes = [];
    if (numWaistPairs > 0) {
      dealCodes.push("WCB1G1");
    }
    if (numGroupsOf5 > 0) {
      for (let i = 0; i < numGroupsOf5; i++) {
        dealCodes.push("BIG5");
      }
    }
    if (remainder === 4) {
      dealCodes.push("BIG4");
    } else if (remainder === 3) {
      dealCodes.push("BIG3");
    } else if (remainder === 2) {
      dealCodes.push("B1G1");
    }
    dealCode = dealCodes.join(",");

    // Build active deal name (with '+' combining)
    let dealParts = [];
    if (numWaistPairs > 0) {
      dealParts.push("Waist Chain B1G1");
    }
    if (numGroupsOf5 > 0) {
      for (let i = 0; i < numGroupsOf5; i++) {
        dealParts.push("5@₹1,599");
      }
    }
    if (remainder === 4) {
      dealParts.push("4@₹1,299");
    } else if (remainder === 3) {
      dealParts.push("3@₹899");
    } else if (remainder === 2) {
      dealParts.push("B1G1");
    }

    if (dealParts.length > 0) {
      activeDealName = dealParts.join(" + ") + " — Bundle Deal";
    }

    // Calculate progress and message
    if (totalItems === 0) {
      nextDealMsg = "Add items to unlock deals!";
    } else if (regularPrices.length === 0 && waistPrices.length > 0) {
      if (waistPrices.length % 2 === 1) {
        nextDealProgress = 50;
        nextDealMsg = "Add 1 more Waist Chain to get it FREE! (B1G1)";
        offersIcon = "✨";
      } else {
        nextDealProgress = 100;
        nextDealMsg = `Waist Chain B1G1 Applied! You save ${formatMoneyValue(totalDiscount)}!`;
        offersIcon = "✅";
      }
    } else if (remainder === 0) {
      nextDealProgress = 100;
      nextDealMsg = `Best Deal Applied! You save ${formatMoneyValue(totalDiscount)}!`;
      offersIcon = "✅";
    } else if (remainder === 1) {
      nextDealProgress = 20;
      nextDealMsg = "Add 1 more item to get 1 FREE! (B1G1)";
      offersIcon = "🎉";
    } else if (remainder === 2) {
      nextDealProgress = 40;
      nextDealMsg = "B1G1 Applied! Add 1 more for 3 @ ₹899!";
      offersIcon = "🔥";
    } else if (remainder === 3) {
      nextDealProgress = 60;
      nextDealMsg = "Buy 3 @ ₹899 Applied! Add 1 more item for 4 @ ₹1,299!";
      offersIcon = "⚡";
    } else if (remainder === 4) {
      nextDealProgress = 80;
      nextDealMsg = "Buy 4 @ ₹1,299 Applied! Add 1 more item for 5 @ ₹1,599!";
      offersIcon = "💎";
    }

    const savingsAmount = totalDiscount;
    const mysteryThreshold = 199900;
    let mysteryProgress = 0;
    let mysteryDifference = 0;
    if (customSubtotal >= mysteryThreshold) {
      mysteryProgress = 100;
      mysteryDifference = 0;
    } else {
      mysteryDifference = mysteryThreshold - customSubtotal;
      mysteryProgress = Math.floor((customSubtotal * 100) / mysteryThreshold);
    }

    // 4. Update the row's total price immediately
    const rowLinePriceText = row.querySelector('.cart-items__price text-component');
    if (rowLinePriceText) {
      const itemOriginalPrice = parseInt(row.dataset.originalPrice) || 0;
      const newLinePrice = quantity * itemOriginalPrice;
      const formattedPrice = formatMoneyValue(newLinePrice);
      rowLinePriceText.textContent = formattedPrice;
      rowLinePriceText.setAttribute('value', formattedPrice);
    }

    // 5. Update summary Subtotal, Savings and Total
    const subtotalValueEl = document.querySelector('.cart-totals__subtotal-value');
    if (subtotalValueEl) {
      subtotalValueEl.textContent = formatMoneyValue(originalSubtotal);
    }

    const savingsValueEl = document.querySelector('.cart-totals__savings-value');
    const savingsBadgeEl = document.querySelector('.cart-totals__savings-badge');
    if (savingsValueEl) {
      savingsValueEl.textContent = `-${formatMoneyValue(savingsAmount)}`;
    }
    if (savingsBadgeEl) {
      savingsBadgeEl.textContent = activeDealName;
    }

    const subtotalRow = document.querySelector('.cart-totals__subtotal');
    const savingsRow = document.querySelector('.cart-totals__savings');
    if (subtotalRow) {
      subtotalRow.style.display = savingsAmount > 0 ? '' : 'none';
    }
    if (savingsRow) {
      savingsRow.style.display = savingsAmount > 0 ? '' : 'none';
    }

    if (this.refs.cartTotal) {
      const formattedTotal = formatMoneyValue(customSubtotal);
      this.refs.cartTotal.textContent = formattedTotal;
      this.refs.cartTotal.setAttribute('value', formattedTotal);
    }

    const vaahiniSavingsEl = document.getElementById('vaahini-savings');
    if (vaahiniSavingsEl) {
      vaahiniSavingsEl.value = String(savingsAmount);
    }
    const vaahiniExpectedTotalEl = document.getElementById('vaahini-expected-total');
    if (vaahiniExpectedTotalEl) {
      vaahiniExpectedTotalEl.value = String(customSubtotal);
    }

    const checkoutBtn = document.getElementById('checkout') || document.querySelector('.cart__checkout-button') || (typeof window.vaahiniGetCheckoutBtn === 'function' ? window.vaahiniGetCheckoutBtn() : null);
    if (checkoutBtn) {
      checkoutBtn.dataset.savings = String(savingsAmount);
      checkoutBtn.dataset.expectedTotal = String(customSubtotal);
    }

    if (!window.vaahiniCheckoutButtonLoading) {
      window.vaahiniResetState?.();
    }

    // 6. Update progress bars
    const offersFill = document.querySelector('.cart-promo-bar__fill--offers');
    const offersIconEl = document.querySelector('.cart-promo-row__icon');
    const offersText = offersFill?.closest('.cart-promo-row')?.querySelector('.cart-promo-row__text');

    if (offersFill) offersFill.style.width = `${nextDealProgress}%`;
    if (offersIconEl) offersIconEl.textContent = offersIcon;
    if (offersText) offersText.textContent = nextDealMsg;

    const giftFill = document.querySelector('.cart-promo-bar__fill--gift');
    const giftText = giftFill?.closest('.cart-promo-row')?.querySelector('.cart-promo-row__text');

    if (giftFill) giftFill.style.width = `${mysteryProgress}%`;
    if (giftText) {
      if (mysteryDifference > 0) {
        giftText.innerHTML = `Add ${formatMoneyValue(mysteryDifference)} more for a FREE mystery gift!`;
      } else {
        giftText.innerHTML = `<span class="cart-promo-row__text--unlocked">🎁 Mystery Gift Unlocked!</span>`;
      }
    }

    // 7. Update deal banner
    const dealBanner = document.querySelector('.cart-deal-banner');
    const dealBannerText = document.querySelector('.cart-deal-banner__text');
    if (dealBanner) {
      if (savingsAmount > 0) {
        dealBanner.style.display = '';
        if (dealBannerText) {
          let bannerText = "";
          if (totalItems === 2) {
            bannerText = `Buy 1 Get 1 Free — You save ${formatMoneyValue(savingsAmount)}!`;
          } else if (totalItems === 3) {
            bannerText = `3 for ₹899 — You save ${formatMoneyValue(savingsAmount)}!`;
          } else if (totalItems === 4) {
            bannerText = `4 for ₹1,299 — You save ${formatMoneyValue(savingsAmount)}!`;
          } else if (totalItems === 5) {
            bannerText = `5 for ₹1,599 — You save ${formatMoneyValue(savingsAmount)}!`;
          } else if (totalItems > 5) {
            bannerText = `${activeDealName} — You save ${formatMoneyValue(savingsAmount)}!`;
          }
          dealBannerText.textContent = bannerText;
        }
      } else {
        dealBanner.style.display = 'none';
      }
    }

  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
